import { useAuth } from '../../auth.context';
import { useMemo } from 'react';
import * as kypApiService from '../../services/kypApiService';
import {
  PlayerNameMatch,
  DetailedPlayerMatch,
  PlayerData
} from '../types/kyp-search.types';

export interface SearchServiceDeps {
  getToken: () => string | null;
  signal?: AbortSignal;
}

export class SearchService {
  constructor(private deps: SearchServiceDeps) {}

  async searchPlayersByName(name: string): Promise<{
    basicMatches: PlayerNameMatch[];
    detailedMatches: DetailedPlayerMatch[];
  }> {
    if (!name.trim()) {
      throw new Error('Please enter a player name');
    }

    try {
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Error: Not authenticated');
      }

      // Try to get detailed player matches first using the new API
      try {
        console.log('Fetching detailed player matches');
        const detailedMatches = await kypApiService.searchPlayers(name, token);
        
        if (detailedMatches && detailedMatches.length > 0) {
          console.log('Found detailed player matches:', detailedMatches);
          
          // Also create basic matches for backward compatibility
          const basicMatches = detailedMatches.map(player => ({
            id: player.id,
            name: player.name
          }));
          
          return {
            basicMatches,
            detailedMatches
          };
        }
      } catch (detailedSearchError) {
        console.error('Error fetching detailed player matches, falling back to basic search:', detailedSearchError);
        // Continue with the basic search as fallback
      }

      // Fallback to basic player search if detailed search fails or returns no results
      console.log('Falling back to basic player search');
      const basicMatches = await kypApiService.fetchPlayerMatches(name, token);
      
      return {
        basicMatches,
        detailedMatches: []
      };
    } catch (error) {
      // Don't rethrow if the request was aborted
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Name search request was aborted');
        return {
          basicMatches: [],
          detailedMatches: []
        };
      }
      
      console.error('Name search error:', error);
      throw error;
    }
  }

  async checkAutomatedResults(id: string, options?: { 
    updateState?: boolean 
  }): Promise<{
    hasResults: boolean;
    data?: PlayerData;
    timestamp?: string;
  }> {
    try {
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Error: Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      console.log(`Checking for automated results for player ID: ${id}`);
      const response = await fetch(`${baseUrl}/ai/v1/player/${id}/automated_search_results`, {
        headers: requestHeaders,
        signal: this.deps.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.has_automated_results) {
        console.log('Found automated search results from', new Date(data.search_timestamp).toLocaleString(), ':', data);
        
        // Process the data to ensure proper format
        const processedData: PlayerData = {};
        
        // Remove metadata fields that aren't part of the PlayerData structure
        const { 
          status, 
          message, 
          has_automated_results, 
          is_automated, 
          search_id, 
          search_timestamp, 
          ...playerDataFields 
        } = data;
        
        // Process each category in the results
        Object.entries(playerDataFields).forEach(([key, value]) => {
          if (typeof value === 'string') {
            try {
              // Try to parse the string as JSON
              const parsedValue = JSON.parse(value);
              processedData[key as keyof PlayerData] = parsedValue;
            } catch (e) {
              // If parsing fails, use the string value directly
              processedData[key as keyof PlayerData] = { summary: value };
            }
          } else {
            // If it's already an object, use it directly
            processedData[key as keyof PlayerData] = value as any;
          }
        });
        
        return {
          hasResults: true,
          data: processedData,
          timestamp: data.search_timestamp
        };
      }
      
      console.log('No automated search results found');
      return {
        hasResults: false
      };
    } catch (error) {
      console.error('Error checking for automated results:', error);
      return {
        hasResults: false
      };
    }
  }
}

export const useSearchService = () => {
  const { getToken } = useAuth();
  
  return useMemo(() => {
    const abortController = new AbortController();
    
    const service = new SearchService({ 
      getToken, 
      signal: abortController.signal 
    });
    
    // Create an object that has all the methods from the service
    // plus an abort method, properly preserving the prototype chain
    const serviceWithAbort = Object.create(Object.getPrototypeOf(service), {
      ...Object.getOwnPropertyDescriptors(service),
      abort: {
        value: () => {
          abortController.abort();
        },
        enumerable: true,
        configurable: true,
        writable: true
      }
    });
    
    return serviceWithAbort;
  }, [getToken]);
};